import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Users, ClipboardCheck, AlertTriangle, Clock, ArrowRight, UserCheck, UserX, Pause, Flag } from "lucide-react";
import { api } from "../api";
import type { DashboardStats, Member } from "../types";

function fmtDate(d: string | null): string {
  if (!d) return "\u2014";
  const raw = d.includes("T") ? d.split("T")[0] : d;
  const parts = raw.split("-");
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/** Calculate calendar week number from start date to today */
function weeksSince(startDate: string | null): number {
  if (!startDate) return 0;
  const raw = startDate.includes("T") ? startDate.split("T")[0] : startDate;
  const start = new Date(raw + "T00:00:00Z");
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7));
}

/** Get the Monday of the week that is N weeks from now */
function dateInWeeks(weeks: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentIssues, setPaymentIssues] = useState<any[]>([]);
  const [allMembers, setAllMembers] = useState<Member[]>([]);

  useEffect(() => {
    api
      .getDashboard()
      .then(setStats)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    api.getPaymentIssues().then((r) => setPaymentIssues(r.members)).catch(() => {});
    api.getMembers().then((r) => setAllMembers(r.members)).catch(() => {});
  }, []);

  // Week 11 flag: members currently at week 9 (will reach week 11 in ~2 weeks)
  const week11Flags = useMemo(() => {
    return allMembers
      .filter((m) => {
        if (m.status !== "Active" && m.status !== "Paused" && m.status !== "Payment Issues") return false;
        const wk = weeksSince(m.start_date);
        return wk === 9;
      })
      .map((m) => {
        const week11Date = dateInWeeks(2);
        return { ...m, week11Date };
      })
      .sort((a, b) => (a.name < b.name ? -1 : 1));
  }, [allMembers]);

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-100 rounded w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );

  if (error)
    return (
      <div className="text-center py-20 text-red-500">
        <p className="text-lg">Failed to load dashboard</p>
        <p className="text-sm mt-2 text-gray-400">{error}</p>
      </div>
    );

  if (!stats) return null;

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Primary stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/members" className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow group">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-[#e2c4b6]/30">
              <Users className="w-5 h-5 text-[#5f1b2c]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total_members}</p>
              <p className="text-sm text-gray-500">Total Members</p>
            </div>
          </div>
        </Link>

        <Link to="/checkins" className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow group">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-50">
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{stats.active_members}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </Link>

        <Link to="/members?status=Paused" className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-amber-50">
              <Pause className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{stats.paused_members}</p>
              <p className="text-sm text-gray-500">Paused</p>
            </div>
          </div>
        </Link>

        <Link to="/members?status=Payment Issues" className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-red-50">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-500">{stats.payment_issues}</p>
              <p className="text-sm text-gray-500">Payment Issues</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Expired / Former */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/members?status=Expired/Former" className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-gray-100">
              <UserX className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-500">{stats.expired_former}</p>
              <p className="text-sm text-gray-500">Expired / Former</p>
            </div>
          </div>
        </Link>

        {/* Check-in stats for this week */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-[#e2c4b6]/30">
              <ClipboardCheck className="w-5 h-5 text-[#5f1b2c]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.submitted_checkins} <span className="text-base font-normal text-gray-400">/ {stats.expected_checkins}</span></p>
              <p className="text-sm text-gray-500">Check-Ins This Week</p>
            </div>
          </div>
        </div>

        <Link to="/checkins?filter=missing" className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-orange-50">
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-500">{stats.missing_checkins}</p>
              <p className="text-sm text-gray-500">Missing This Week</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Quick links */}
      <div className="grid md:grid-cols-2 gap-6">
        <Link to="/members" className="bg-[#e2c4b6]/20 border border-[#e2c4b6] rounded-xl p-6 hover:bg-[#e2c4b6]/30 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Members</h3>
              <p className="text-sm text-gray-500 mt-1">View and manage all members</p>
            </div>
            <ArrowRight className="w-5 h-5 text-[#5f1b2c] group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link to="/checkins" className="bg-[#5f1b2c]/5 border border-[#5f1b2c]/20 rounded-xl p-6 hover:bg-[#5f1b2c]/10 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Weekly Check-Ins</h3>
              <p className="text-sm text-gray-500 mt-1">Track this week's check-in status</p>
            </div>
            <ArrowRight className="w-5 h-5 text-[#5f1b2c] group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link to="/analytics" className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow group">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
              <p className="text-sm text-gray-500 mt-1">Compliance, growth, and membership breakdown</p>
            </div>
            <ArrowRight className="w-5 h-5 text-[#5f1b2c] group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link to="/analytics#expiring" className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow group">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Expiring Soon</h3>
              <p className="text-sm text-gray-500 mt-1">Memberships ending within 30 days</p>
            </div>
            <ArrowRight className="w-5 h-5 text-[#5f1b2c] group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </div>

      {/* Week 11 Flags */}
      {week11Flags.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-amber-700 flex items-center gap-2 mb-3">
            <Flag className="w-4 h-4" />Week 11 Approaching (Currently Week 9)
          </h3>
          <p className="text-xs text-amber-600 mb-3">These members will reach their 11th week on <strong>{dateInWeeks(2)}</strong>. Prepare for their milestone.</p>
          <div className="space-y-2">
            {week11Flags.map((m) => (
              <Link key={m.id} to={`/members/${m.id}`} className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 hover:shadow-sm transition-shadow border border-amber-100">
                <span className="text-sm font-medium text-gray-900">{m.name}</span>
                <span className="text-xs text-amber-600">Week 11 date: {m.week11Date}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Payment Issues List */}
      {paymentIssues.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-red-700 flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4" />Payment Issues ({paymentIssues.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-red-600/70 border-b border-red-200/50">
                  <th className="px-3 py-2 font-medium">Member</th>
                  <th className="px-3 py-2 font-medium">Plan</th>
                  <th className="px-3 py-2 font-medium text-right">Follow-Up Date</th>
                </tr>
              </thead>
              <tbody>
                {paymentIssues.map((m: any) => (
                  <tr key={m.id} className="border-b border-red-100/50 last:border-0">
                    <td className="px-3 py-2.5">
                      <Link to={`/members/${m.id}`} className="font-medium text-gray-900 hover:text-[#5f1b2c] hover:underline">{m.name}</Link>
                    </td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs">{m.payment_plan || "\u2014"}</td>
                    <td className="px-3 py-2.5 text-right">
                      {m.follow_up_date ? (
                        <span className="text-xs text-red-600 font-medium">{fmtDate(m.follow_up_date)}</span>
                      ) : (
                        <span className="text-xs text-gray-400">Not set</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}